import React, { useEffect, useState } from 'react';
import { Divider, EmptyText, Row, Stack, Text, Title } from 'library/ui';
import { useClient } from 'shared';
import { LocationsDropDown } from './components/LocationsDropDown';
import { TimesheetsTable } from './components/TimesheetsTable';

import type { PagingResult, IBusinessLocation, ISentHomeAppointment } from 'models';
import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/types';
import { AddRetroHoursModal } from './components/AddRetroHoursModal';
import SentHomeCancels from './components/SentHomeCancels';
import { CenterMissingPaymentReports } from './components/CenterMissingPaymentReports';

export default function Timesheets() {
    const paramLocation = new URLSearchParams(window.location.search).get('location');
    const paramPage = new URLSearchParams(window.location.search).get('page');
    const paramUnassociatedPage = new URLSearchParams(window.location.search).get('unassociatedPage');
    const page = paramPage ? Number(paramPage) : 1;
    const unassociatedPage = paramUnassociatedPage ? Number(paramUnassociatedPage) : 1;

    const [firstLocation, setFirstLocation] = useState<IBusinessLocation | undefined>();

    const {
        loading: locationsLoading,
        data: locationsData,
        error: locationsError,
    } = useClient<PagingResult<IBusinessLocation>>({
        url: 'api/business-location/?page_size=100',
    });

    useEffect(() => {
        if (locationsData?.results?.length) {
            setFirstLocation(locationsData.results[0]);
        }
    }, [locationsData]);

    const {
        send: fetchTimesheets,
        data: timesheets,
        loading: timesheetsLoading,
        error: timesheetsError,
    } = useClient<PagingResult<TimesheetWorker>>({
        url: `api/hour-report/timesheets/?location=${paramLocation || firstLocation?.id}&page=${page}`,
        options: { sendImmediately: false },
    });

    const {
        send: fetchShiftsNeedingClockedIn,
        data: shiftsNeedingClockedIn,
        loading: shiftsNeedingClockedInLoading,
        error: shiftsNeedingClockedInError,
    } = useClient<PagingResult<UnclockedInTimesheetWorker>>({
        url: `api/hour-report/shifts-to-clockin/?location=${paramLocation || firstLocation?.id}&page=${unassociatedPage}`,
        options: { sendImmediately: false },
    });

    const { send: fetchSentHomeCancellations, data: sentHomeCancellations } = useClient<ISentHomeAppointment[]>({
        url: `api/sub-shifts/sent-home-appts/?business_location_id=${paramLocation || firstLocation?.id}`,
        options: { sendImmediately: false },
    });

    useEffect(() => {
        const locationId = paramLocation || firstLocation?.id;
        if (!locationId) return;

        fetchTimesheets();
        fetchShiftsNeedingClockedIn();
        fetchSentHomeCancellations();
    }, [firstLocation?.id, paramLocation, page, unassociatedPage]);

    function setPage(newPage: number) {
        const params = new URLSearchParams(window.location.search);
        params.set('page', newPage.toString());
        const url = `timesheets?${params.toString()}`;
        window.location.href = url;
    }

    function setUnassociatedPage(newPage: number) {
        const params = new URLSearchParams(window.location.search);
        params.set('unassociatedPage', newPage.toString());
        const url = `timesheets?${params.toString()}`;
        window.location.href = url;
    }

    const locationSelected = paramLocation || firstLocation?.id;

    return (
        <Stack gap="lg">
            <Title size="h1">Timesheets</Title>
            {!(locationsError || timesheetsError || shiftsNeedingClockedInError) &&
                (locationsLoading ? (
                    <EmptyText>Loading...</EmptyText>
                ) : (
                    <>
                        <Row justify="between" align="center" gap="md" wrap>
                            <LocationsDropDown data={locationsData} location={firstLocation?.id.toString()} />
                            <AddRetroHoursModal businessLocations={locationsData} onComplete={fetchTimesheets} />
                        </Row>
                        <Divider />
                        {locationSelected && (
                            <Stack gap="lg">
                                {sentHomeCancellations?.length ? (
                                    <>
                                        <SentHomeCancels
                                            overallReasons={sentHomeCancellations}
                                            refreshReasons={fetchSentHomeCancellations}
                                        />
                                        <Divider />
                                    </>
                                ) : null}
                                {!!shiftsNeedingClockedIn?.results?.length && (
                                    <>
                                        <Stack gap="md">
                                            <Stack gap="sm">
                                                <Title size="h2">
                                                    Workers Not Clocked In ({shiftsNeedingClockedIn?.results?.length})
                                                </Title>
                                                <Text size="sm">
                                                    If these worker(s) are at the center, please clock them in.
                                                </Text>
                                            </Stack>
                                            <TimesheetsTable
                                                data={shiftsNeedingClockedIn}
                                                loading={shiftsNeedingClockedInLoading}
                                                refresh={() => {
                                                    fetchShiftsNeedingClockedIn();
                                                    fetchTimesheets();
                                                }}
                                                page={unassociatedPage}
                                                setPage={setUnassociatedPage}
                                            />
                                        </Stack>
                                        <Divider />
                                    </>
                                )}
                                <CenterMissingPaymentReports
                                    locationId={locationSelected}
                                    timesheetRefresh={fetchTimesheets}
                                />
                                <Stack gap="md">
                                    <Stack gap="sm">
                                        <Title size="h2">Recent Timesheets ({timesheets?.results?.length || 0})</Title>
                                        {timesheets?.results && timesheets.results.length > 0 && (
                                            <Text size="sm">
                                                These are the timesheets for the past week of shifts. Please review
                                                anything that says &quot;Needs Review&quot;.
                                            </Text>
                                        )}
                                    </Stack>
                                    <TimesheetsTable
                                        data={timesheets}
                                        loading={timesheetsLoading}
                                        refresh={fetchTimesheets}
                                        page={page}
                                        setPage={setPage}
                                    />
                                </Stack>
                            </Stack>
                        )}
                    </>
                ))}
            {(locationsError || timesheetsError) && <Text tone="error">Error loading data</Text>}
        </Stack>
    );
}
